<template>
  <DataCard
    title="Images et médias"
    icon="fas fa-images"
    icon-color="text-orange-600"
    icon-bg-color="bg-orange-100"
    class="w-full"
  >
    <template #header-actions>
      <button
        @click="$emit('save')"
        :disabled="saving || disableSave"
        class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        <i v-if="saving" class="fas fa-spinner fa-spin"></i>
        <i v-else class="fas fa-save"></i>
        <span>{{ saving ? "Sauvegarde..." : "Sauvegarder" }}</span>
      </button>
    </template>
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <!-- Bannières -->
      <div class="space-y-4">
        <h3 class="text-lg font-semibold text-black">Bannières</h3>
        <div class="space-y-2">
          <p class="text-sm text-black">
            L'image doit être au format JPG et inférieure à 2 Mo, dimension conseillée : 1500x590 pixels
          </p>
          
            <!-- Aperçu de la bannière actuelle -->
            <div class="mt-4">
              <p class="text-sm text-black mb-2">Image actuelle :</p>
              <div class="relative w-full max-w-md h-36">
                <img 
                  v-if="showBanner"
                  :src="currentBannerUrl"
                  alt="Bannière actuelle"
                  class="w-full h-full object-contain rounded-lg border border-gray-300 bg-white"
                  @error="showBanner = false"
                />
                <div v-else class="w-full h-full flex items-center justify-center rounded-lg border border-dashed border-gray-300 text-black bg-gray-50">
                  Aucune bannière
                </div>
              </div>
            </div>
          
          <div class="w-full max-w-md flex items-center gap-3">
            <input
              type="file"
              accept=".jpg,.jpeg"
              @change="handleBannerUpload"
              class="block w-full text-sm text-black file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            <button
              v-if="showBanner"
              type="button"
              @click="deleteBanner"
              class="flex-shrink-0 px-3 py-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 text-sm font-medium flex items-center gap-1"
            >
              <i class="fas fa-trash text-xs"></i>
              Supprimer
            </button>
          </div>
        </div>
      </div>

      <!-- Affiches -->
      <div class="space-y-4">
        <h3 class="text-lg font-semibold text-black">Affiches</h3>
        <div class="space-y-2">
          <p class="text-sm text-black">
            L'image doit être au format JPG et inférieure à 2 Mo, dimension conseillée : 250x192 pixels
          </p>
          
          <!-- Aperçu de l'affiche actuelle -->
          <div class="mt-4">
            <p class="text-sm text-black mb-2">Image actuelle :</p>
            <div class="relative w-full max-w-md h-36">
                <img 
                  v-if="showSquare"
                  :src="currentSquareUrl"
                  alt="Affiche actuelle"
                  class="w-full h-full object-contain rounded-lg border border-gray-300 bg-white"
                  @error="showSquare = false"
                />
                <div v-else class="w-full h-full flex items-center justify-center rounded-lg border border-dashed border-gray-300 text-black bg-gray-50">
                  Aucune affiche
                </div>
            </div>
          </div>
          
          <div class="w-full max-w-md flex items-center gap-3">
            <input
              type="file"
              accept=".jpg,.jpeg"
              @change="handlePosterUpload"
              class="block w-full text-sm text-black file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            <button
              v-if="showSquare"
              type="button"
              @click="deletePoster"
              class="flex-shrink-0 px-3 py-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 text-sm font-medium flex items-center gap-1"
            >
              <i class="fas fa-trash text-xs"></i>
              Supprimer
            </button>
          </div>
        </div>
      </div>

      <!-- Logo -->
      <div class="space-y-4">
        <h3 class="text-lg font-semibold text-black">Logo</h3>
        <div class="space-y-2">
          <p class="text-sm text-black">
            L'image doit être au format JPG et inférieure à 2 Mo, limite dimension : 345px max de largeur
          </p>
          
          <!-- Aperçu du logo actuel -->
          <div class="mt-4">
            <p class="text-sm text-black mb-2">Image actuelle :</p>
            <div class="relative w-full max-w-md h-36">
                <img 
                  v-if="showLogo"
                  :src="currentLogoUrl"
                  alt="Logo actuel"
                  class="w-full h-full object-contain rounded-lg border border-gray-300 bg-white"
                  @error="showLogo = false"
                />
                <div v-else class="w-full h-full flex items-center justify-center rounded-lg border border-dashed border-gray-300 text-black bg-gray-50">
                  Aucun logo
                </div>
            </div>
          </div>
          
          <div class="w-full max-w-md flex items-center gap-3">
            <input
              type="file"
              accept=".jpg,.jpeg"
              @change="handleLogoUpload"
              class="block w-full text-sm text-black file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            <button
              v-if="showLogo"
              type="button"
              @click="deleteLogo"
              class="flex-shrink-0 px-3 py-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 text-sm font-medium flex items-center gap-1"
            >
              <i class="fas fa-trash text-xs"></i>
              Supprimer
            </button>
          </div>
        </div>
      </div>
    </div>
  </DataCard>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { DataCard } from '@/components'
import { ImageUrls, UploadedImages } from '@/types/generalInformation'

interface Props {
  imageUrls?: ImageUrls
  saving?: boolean
  disableSave?: boolean
}

interface Emits {
  (e: 'images-changed', images: UploadedImages): void
  (e: 'save'): void
  (e: 'delete-image', type: 'banniere' | 'square' | 'logo-boutique'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// URLs des images depuis les props
const logoUrl = computed(() => props.imageUrls?.logo || '')
const banniereUrl = computed(() => props.imageUrls?.banniere || '')
const squareUrl = computed(() => props.imageUrls?.square || '')

// Visibilité des aperçus (masque si l'image n'existe pas ou échoue à charger)
const showBanner = ref(!!banniereUrl.value)
const showSquare = ref(!!squareUrl.value)
const showLogo = ref(!!logoUrl.value)

// Prévisualisation locale des images sélectionnées
const bannerPreviewUrl = ref<string>('')
const squarePreviewUrl = ref<string>('')
const logoPreviewUrl = ref<string>('')

const currentBannerUrl = computed(() => bannerPreviewUrl.value || banniereUrl.value)
const currentSquareUrl = computed(() => squarePreviewUrl.value || squareUrl.value)
const currentLogoUrl = computed(() => logoPreviewUrl.value || logoUrl.value)

// Réagir aux changements d'URLs (ex: rechargement après sauvegarde)
watch(banniereUrl, (v) => {
  // Si une nouvelle URL serveur arrive, on bascule dessus et on nettoie la prévisualisation locale
  if (v) {
    if (bannerPreviewUrl.value) {
      URL.revokeObjectURL(bannerPreviewUrl.value)
      bannerPreviewUrl.value = ''
    }
  }
  showBanner.value = !!v || !!bannerPreviewUrl.value
})
watch(squareUrl, (v) => {
  if (v && squarePreviewUrl.value) {
    URL.revokeObjectURL(squarePreviewUrl.value)
    squarePreviewUrl.value = ''
  }
  showSquare.value = !!v || !!squarePreviewUrl.value
})
watch(logoUrl, (v) => {
  if (v && logoPreviewUrl.value) {
    URL.revokeObjectURL(logoPreviewUrl.value)
    logoPreviewUrl.value = ''
  }
  showLogo.value = !!v || !!logoPreviewUrl.value
})

// État des images uploadées
const uploadedImages = ref<UploadedImages>({
  logo: null,
  banniere: null,
  square: null
})

// Fonction de validation de la taille des fichiers (2 Mo max)
const validateFileSize = (file: File, type: string): boolean => {
  const maxSize = 2 * 1024 * 1024 // 2 Mo en bytes
  if (file.size > maxSize) {
    alert(`❌ Le fichier ${type} est trop volumineux. Taille maximale autorisée : 2 Mo`)
    return false
  }
  return true
}

// Handlers d'upload
const handleBannerUpload = (event: Event) => {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (file) {
    if (validateFileSize(file, 'bannière')) {
      uploadedImages.value.banniere = file
      // Mettre à jour l'aperçu local immédiatement
      if (bannerPreviewUrl.value) {
        URL.revokeObjectURL(bannerPreviewUrl.value)
      }
      bannerPreviewUrl.value = URL.createObjectURL(file)
      showBanner.value = true
      emit('images-changed', uploadedImages.value)
      nextTick(() => emit('save'))
    } else {
      // Réinitialiser l'input si le fichier est trop volumineux
      (event.target as HTMLInputElement).value = ''
    }
  }
}

const handlePosterUpload = (event: Event) => {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (file) {
    if (validateFileSize(file, 'affiche')) {
      uploadedImages.value.square = file
      // Prévisualisation immédiate
      if (squarePreviewUrl.value) {
        URL.revokeObjectURL(squarePreviewUrl.value)
      }
      squarePreviewUrl.value = URL.createObjectURL(file)
      showSquare.value = true
      emit('images-changed', uploadedImages.value)
      nextTick(() => emit('save'))
    } else {
      // Réinitialiser l'input si le fichier est trop volumineux
      (event.target as HTMLInputElement).value = ''
    }
  }
}

const handleLogoUpload = (event: Event) => {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (file) {
    if (validateFileSize(file, 'logo-boutique')) {
      uploadedImages.value.logo = file
      // Prévisualisation immédiate
      if (logoPreviewUrl.value) {
        URL.revokeObjectURL(logoPreviewUrl.value)
      }
      logoPreviewUrl.value = URL.createObjectURL(file)
      showLogo.value = true
      emit('images-changed', uploadedImages.value)
      nextTick(() => emit('save'))
    } else {
      // Réinitialiser l'input si le fichier est trop volumineux
      (event.target as HTMLInputElement).value = ''
    }
  }
}


// Handlers de suppression
const deleteBanner = () => {
  uploadedImages.value.banniere = null
  if (bannerPreviewUrl.value) {
    URL.revokeObjectURL(bannerPreviewUrl.value)
    bannerPreviewUrl.value = ''
  }
  showBanner.value = false
  emit('delete-image', 'banniere')
}

const deletePoster = () => {
  uploadedImages.value.square = null
  if (squarePreviewUrl.value) {
    URL.revokeObjectURL(squarePreviewUrl.value)
    squarePreviewUrl.value = ''
  }
  showSquare.value = false
  emit('delete-image', 'square')
}

const deleteLogo = () => {
  uploadedImages.value.logo = null
  if (logoPreviewUrl.value) {
    URL.revokeObjectURL(logoPreviewUrl.value)
    logoPreviewUrl.value = ''
  }
  showLogo.value = false
  emit('delete-image', 'logo-boutique')
}

// récupérer les images uploadées 
const getUploadedImages = () => {
  return uploadedImages.value
}

// Exposer la fonction au parent
defineExpose({
  getUploadedImages
})

// Nettoyer l'URL objet lors du démontage du composant
import { onBeforeUnmount } from 'vue'
onBeforeUnmount(() => {
  if (bannerPreviewUrl.value) {
    URL.revokeObjectURL(bannerPreviewUrl.value)
  }
  if (squarePreviewUrl.value) {
    URL.revokeObjectURL(squarePreviewUrl.value)
  }
  if (logoPreviewUrl.value) {
    URL.revokeObjectURL(logoPreviewUrl.value)
  }
})

</script>
